<template>
  <main>
    <!-- Hero section -->
    <div
      class="relative bg-cover bg-center overflow-hidden"
      :style="{
        backgroundImage: `url(${require('../assets/images/mmc-image.jpg')})`,
      }"
    >
      <!-- Overlay with gradient -->
      <div
        class="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-blue/40"
      ></div>

      <!-- Animated background elements -->
      <div class="absolute inset-0 opacity-20">
        <div
          class="absolute top-20 left-10 w-72 h-72 bg-blue rounded-full mix-blend-multiply filter blur-xl animate-blob"
        ></div>
        <div
          class="absolute top-40 right-10 w-72 h-72 bg-sky-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"
        ></div>
        <div
          class="absolute bottom-20 left-40 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"
        ></div>
      </div>

      <div
        class="relative md:px-12 sm:px-12 px-4 max-w-screen-2xl mx-auto z-10 min-h-[85vh] flex items-center text-white"
      >
        <div class="grid lg:grid-cols-2 gap-12 items-center w-full py-20">
          <!-- Left Content -->
          <div class="space-y-8">
            <!-- Badge -->
            <div
              class="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-3 animate__animated animate__fadeInDown"
            >
              <span
                class="w-2 h-2 bg-green-400 rounded-full animate-pulse"
              ></span>
              <span class="text-sm font-medium">8-Week Intensive Program</span>
            </div>

            <!-- Main Headline -->
            <h1
              class="text-5xl md:text-7xl font-bold leading-tight animate__animated animate__fadeInUp"
            >
              Build a Real Business
              <span
                class="block text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500"
              >
                Not a Side Hustle
              </span>
            </h1>

            <!-- Description -->
            <p
              class="text-lg md:text-xl text-gray-200 leading-relaxed max-w-2xl animate__animated animate__fadeInUp animation-delay-200"
            >
              An intensive business leadership program designed to help
              entrepreneurs and executives build structured, scalable, and
              sustainable enterprises.
            </p>

            <!-- CTA Buttons -->
            <div
              class="flex flex-wrap gap-4 animate__animated animate__fadeInUp animation-delay-400"
            >
              <button
                @click="scrollToRegistration"
                class="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue to-sky-600 rounded-xl overflow-hidden shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-blue/50"
              >
                <span class="relative z-10 flex items-center">
                  Apply Now
                  <svg
                    class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
                <div
                  class="absolute inset-0 bg-gradient-to-r from-sky-600 to-blue opacity-0 group-hover:opacity-100 transition-opacity"
                ></div>
              </button>

              <button
                @click="scrollToDetails"
                class="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl hover:bg-white/20 transition-all duration-300"
              >
                Learn More
              </button>
            </div>

            <!-- Stats -->
            <div
              class="grid grid-cols-3 gap-6 pt-8 animate__animated animate__fadeInUp animation-delay-600"
            >
              <div class="text-center lg:text-left">
                <div
                  class="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500"
                >
                  8
                </div>
                <div class="text-sm text-gray-300 mt-1">Weeks</div>
              </div>
              <div class="text-center lg:text-left">
                <div
                  class="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500"
                >
                  50
                </div>
                <div class="text-sm text-gray-300 mt-1">Max Cohort</div>
              </div>
              <div class="text-center lg:text-left">
                <div
                  class="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500"
                >
                  1x
                </div>
                <div class="text-sm text-gray-300 mt-1">Yearly</div>
              </div>
            </div>
          </div>

          <!-- Right Content - Feature Cards -->
          <div
            class="hidden lg:block space-y-6 animate__animated animate__fadeInRight"
          >
            <div
              class="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2"
            >
              <div class="flex items-start space-x-4">
                <div
                  class="w-12 h-12 bg-gradient-to-br from-blue to-sky-600 rounded-xl flex items-center justify-center flex-shrink-0"
                >
                  <i class="fa-solid fa-chart-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 class="text-lg font-semibold mb-2">Scalable Systems</h3>
                  <p class="text-sm text-gray-300">
                    Build businesses with systems that outlive the founder
                  </p>
                </div>
              </div>
            </div>

            <div
              class="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2"
            >
              <div class="flex items-start space-x-4">
                <div
                  class="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0"
                >
                  <i class="fa-solid fa-users-gear text-white text-xl"></i>
                </div>
                <div>
                  <h3 class="text-lg font-semibold mb-2">Expert Leadership</h3>
                  <p class="text-sm text-gray-300">
                    Transform from founder-dependent to professionally managed
                  </p>
                </div>
              </div>
            </div>

            <div
              class="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2"
            >
              <div class="flex items-start space-x-4">
                <div
                  class="w-12 h-12 bg-gradient-to-br from-indigo-500 to-sky-600 rounded-xl flex items-center justify-center flex-shrink-0"
                >
                  <i class="fa-solid fa-certificate text-white text-xl"></i>
                </div>
                <div>
                  <h3 class="text-lg font-semibold mb-2">Certified Program</h3>
                  <p class="text-sm text-gray-300">
                    Graduate with recognized certification and credentials
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modern Wave SVG -->
      <div class="absolute bottom-0 left-0 w-full">
        <svg
          class="w-full h-20 md:h-32"
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            fill="white"
          />
        </svg>
      </div>
    </div>

    <!-- Program Overview Section -->
    <section
      id="details"
      class="container md:px-12 sm:px-12 px-4 lg:px-1 max-w-screen-2xl mx-auto -mt-10"
    >
      <div class="py-20">
        <h2
          class="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-800"
        >
          Program Overview
        </h2>
        <p
          class="text-center text-gray-700 max-w-4xl mx-auto text-lg leading-relaxed"
        >
          The BUM Masterclass is a premium, high-impact business transformation
          program for entrepreneurs and business leaders committed to building
          real businesses with systems that outlive the founder. Through a
          structured curriculum covering leadership mindset, business structure,
          governance, finance, sales, people management, and scaling systems,
          participants gain the clarity and tools required to move from
          founder-dependent operations to professionally managed enterprises.
        </p>

        <!-- Program Details Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          <div
            class="card shadow-lg border border-none rounded-lg p-6 text-center"
          >
            <i class="fa-solid fa-calendar-days text-4xl text-blue mb-3"></i>
            <h3 class="text-xl font-semibold mb-2">Duration</h3>
            <p class="text-gray-700">8 Weeks</p>
          </div>

          <div
            class="card shadow-lg border border-none rounded-lg p-6 text-center"
          >
            <i class="fa-solid fa-location-dot text-4xl text-blue mb-3"></i>
            <h3 class="text-xl font-semibold mb-2">Format</h3>
            <p class="text-gray-700">On-site / Online</p>
          </div>

          <div
            class="card shadow-lg border border-none rounded-lg p-6 text-center"
          >
            <i class="fa-solid fa-users text-4xl text-blue mb-3"></i>
            <h3 class="text-xl font-semibold mb-2">Cohort Size</h3>
            <p class="text-gray-700">Maximum 50 Participants</p>
          </div>

          <div
            class="card shadow-lg border border-none rounded-lg p-6 text-center"
          >
            <i class="fa-solid fa-repeat text-4xl text-blue mb-3"></i>
            <h3 class="text-xl font-semibold mb-2">Frequency</h3>
            <p class="text-gray-700">2 Cohorts Per Year</p>
          </div>

          <div
            class="card shadow-lg border border-none rounded-lg p-6 text-center"
          >
            <i class="fa-solid fa-clock text-4xl text-blue mb-3"></i>
            <h3 class="text-xl font-semibold mb-2">Cohort Periods</h3>
            <div class="mb-3">
              <p class="text-sm font-medium text-gray-600 mb-1">Cohort 1</p>
              <p class="text-gray-700">9th March, 2026 - 30th April, 2026</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600 mb-1">Cohort 2</p>
              <p class="text-gray-700">
                2nd October, 2026 - 19th November, 2026
              </p>
            </div>
          </div>

          <div
            class="card shadow-lg border border-none rounded-lg p-6 text-center"
          >
            <i class="fa-solid fa-naira-sign text-4xl text-blue mb-3"></i>
            <h3 class="text-xl font-semibold mb-2">Program Fee</h3>
            <p class="text-gray-700 font-bold text-xl">₦2,000,000</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Curriculum Snapshot Section-->
    <section
      class="container py-32 md:px-12 sm:px-12 px-4 max-w-screen-2xl mx-auto bg-cover bg-center mb-10 relative"
      :style="{
        backgroundImage: `url(${require('../assets/images/hero-section.jpg')})`,
      }"
    >
      <div class="bg-black bg-opacity-50 absolute inset-0"></div>
      <div class="relative z-10">
        <h1 class="text-3xl md:text-4xl font-bold text-center text-white mb-12">
          Curriculum Snapshot
        </h1>
        <!-- Curriculum Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Curriculum Card 1 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              Foundations of a Scalable Business
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Learn the core principles of building businesses with systems
                that outlive the founder and scale beyond initial operations.
              </h3>
            </div>
          </div>

          <!-- Curriculum Card 2 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              Business Structure & Operating Systems
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Design and implement structured operational frameworks that
                ensure consistency, efficiency, and sustainable growth.
              </h3>
            </div>
          </div>

          <!-- Curriculum Card 3 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              Corporate Governance for SMEs
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Understand governance structures, accountability frameworks, and
                decision-making processes for small and medium enterprises.
              </h3>
            </div>
          </div>

          <!-- Curriculum Card 4 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              Product Development & Value Delivery
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Master the art of creating products and services that deliver
                real value and meet market demands effectively.
              </h3>
            </div>
          </div>

          <!-- Curriculum Card 5 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              Sales, Marketing & Brand Positioning
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Develop strategic approaches to market positioning, customer
                acquisition, and building a compelling brand identity.
              </h3>
            </div>
          </div>

          <!-- Curriculum Card 6 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              Business Finance & Money Management
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Gain mastery in financial planning, budgeting, cash flow
                management, and making informed financial decisions.
              </h3>
            </div>
          </div>

          <!-- Curriculum Card 7 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              People Management & Organizational Culture
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Build high-performing teams and cultivate an organizational
                culture that drives productivity and employee engagement.
              </h3>
            </div>
          </div>

          <!-- Curriculum Card 8 -->
          <div
            class="bg-white card group relative hover:bg-blue hover:text-white shadow-lg border border-none px-4 py-16 cursor-pointer flex flex-col items-center justify-center"
          >
            <h3
              class="text-center text-base font-normal mb-3 group-hover:invisible"
            >
              Scaling, Systems & Succession Planning
            </h3>
            <div
              class="absolute inset-0 flex items-center justify-center bg-blue text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <h3 class="text-center p-4 font-normal text-sm">
                Prepare your business for sustainable growth with scalable
                systems and strategic succession planning frameworks.
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Who This Program Is For Section -->
    <section class="container py-20 px-4 mx-auto max-w-screen-xl">
      <div class="text-center mb-12">
        <h2 class="text-4xl font-bold text-gray-800 sm:text-5xl">
          Who This Program Is For
        </h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div
          class="card shadow-lg border border-none rounded-lg p-8 hover:shadow-xl transition-shadow duration-300"
        >
          <div class="flex items-center mb-4">
            <i class="fa-solid fa-briefcase text-3xl text-blue mr-4"></i>
            <h3 class="text-xl font-semibold text-gray-800">
              Entrepreneurs & Business Owners
            </h3>
          </div>
          <p class="text-gray-700">
            Whether you're just starting or have an established business, gain
            the tools to scale sustainably.
          </p>
        </div>

        <div
          class="card shadow-lg border border-none rounded-lg p-8 hover:shadow-xl transition-shadow duration-300"
        >
          <div class="flex items-center mb-4">
            <i class="fa-solid fa-user-tie text-3xl text-blue mr-4"></i>
            <h3 class="text-xl font-semibold text-gray-800">
              Senior Leaders & Managers
            </h3>
          </div>
          <p class="text-gray-700">
            Seeking advanced leadership capacity to drive organizational
            excellence and transformation.
          </p>
        </div>

        <div
          class="card shadow-lg border border-none rounded-lg p-8 hover:shadow-xl transition-shadow duration-300"
        >
          <div class="flex items-center mb-4">
            <i class="fa-solid fa-rocket text-3xl text-blue mr-4"></i>
            <h3 class="text-xl font-semibold text-gray-800">
              Career Transformers
            </h3>
          </div>
          <p class="text-gray-700">
            Individuals aiming to transform their careers and achieve
            breakthrough business outcomes.
          </p>
        </div>

        <div
          class="card shadow-lg border border-none rounded-lg p-8 hover:shadow-xl transition-shadow duration-300"
        >
          <div class="flex items-center mb-4">
            <i class="fa-solid fa-graduation-cap text-3xl text-blue mr-4"></i>
            <h3 class="text-xl font-semibold text-gray-800">
              Young Professionals
            </h3>
          </div>
          <p class="text-gray-700">
            Seeking structured business advancement and accelerated professional
            growth opportunities.
          </p>
        </div>

        <div
          class="card shadow-lg border border-none rounded-lg p-8 hover:shadow-xl transition-shadow duration-300"
        >
          <div class="flex items-center mb-4">
            <i class="fa-solid fa-lightbulb text-3xl text-blue mr-4"></i>
            <h3 class="text-xl font-semibold text-gray-800">Visionaries</h3>
          </div>
          <p class="text-gray-700">
            Preparing to build or scale sustainable enterprises with long-term
            impact and legacy.
          </p>
        </div>
      </div>
    </section>

    <!-- Eligibility Requirements Section -->
    <section class="container py-20 px-4 mx-auto max-w-screen-xl bg-gray-50">
      <div class="text-center mb-12">
        <div class="inline-block">
          <span
            class="bg-gradient-to-r from-blue to-sky-600 text-white px-6 py-2 rounded-full text-lg font-semibold shadow-lg"
          >
            Eligibility Requirements
          </span>
        </div>
      </div>

      <div class="max-w-4xl mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            class="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
          >
            <div class="flex-shrink-0">
              <div
                class="w-12 h-12 bg-blue rounded-full flex items-center justify-center"
              >
                <i class="fa-solid fa-calendar-check text-white text-xl"></i>
              </div>
            </div>
            <div>
              <h4 class="font-semibold text-gray-800 mb-2">
                Full Availability
              </h4>
              <p class="text-gray-600">
                Availability to attend all 8 weeks of the program and the format
                is: On-site / Online
              </p>
            </div>
          </div>

          <div
            class="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
          >
            <div class="flex-shrink-0">
              <div
                class="w-12 h-12 bg-blue rounded-full flex items-center justify-center"
              >
                <i class="fa-solid fa-heart text-white text-xl"></i>
              </div>
            </div>
            <div>
              <h4 class="font-semibold text-gray-800 mb-2">Full Commitment</h4>
              <p class="text-gray-600">
                Commitment to full participation and engagement
              </p>
            </div>
          </div>

          <div
            class="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 md:col-span-2"
          >
            <div class="flex-shrink-0">
              <div
                class="w-12 h-12 bg-blue rounded-full flex items-center justify-center"
              >
                <i class="fa-solid fa-building text-white text-xl"></i>
              </div>
            </div>
            <div>
              <h4 class="font-semibold text-gray-800 mb-2">
                Business Ownership or Leadership Role
              </h4>
              <p class="text-gray-600">
                Ownership of or leadership role in a business or organization
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Graduation & Certification Section -->
    <section class="container py-20 px-4 mx-auto max-w-screen-xl">
      <div class="max-w-4xl mx-auto text-center">
        <div
          class="bg-gradient-to-br from-blue to-sky-600 rounded-2xl p-12 shadow-2xl text-white"
        >
          <i class="fa-solid fa-certificate text-6xl mb-6"></i>
          <h2 class="text-3xl md:text-4xl font-bold mb-4">
            Graduation & Certification
          </h2>
          <p class="text-lg md:text-xl leading-relaxed">
            Participants who complete the program will graduate and receive a
            certificate of participation, recognizing their commitment to
            building structured, scalable, and sustainable businesses.
          </p>
        </div>
      </div>
    </section>

    <!-- Registration Form Section -->
    <section
      id="registration"
      class="container py-20 px-4 mx-auto max-w-screen-xl bg-gradient-to-br from-gray-50 to-gray-100"
    >
      <div class="text-center mb-12">
        <h2 class="text-4xl font-bold text-gray-800 sm:text-5xl mb-4">
          Register for BUM Masterclass
        </h2>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Complete your registration and secure your spot in the next cohort.
        </p>
      </div>

      <div class="max-w-5xl mx-auto">
        <!-- Program Fee Display - Single Instance -->
        <div
          class="bg-gradient-to-br from-blue to-sky-600 text-white rounded-xl p-6 mb-8 text-center shadow-lg"
        >
          <p class="text-lg mb-2">Program Fee</p>
          <p class="text-4xl font-bold" v-if="!isLoadingCost">
            {{ bumMasterclassCost }}
          </p>
          <p class="text-2xl" v-else>
            <span class="animate-pulse">Loading...</span>
          </p>
          <p class="text-sm mt-2 opacity-90">
            One-time payment for full 8-week program
          </p>
        </div>

        <!-- Registration Form Card -->
        <div class="bg-white rounded-2xl shadow-xl p-6 md:p-10">
          <form @submit.prevent="submitRegistration" class="space-y-10">
            <!-- Section 1: Personal Information -->
            <div class="space-y-6">
              <div
                class="flex items-center space-x-3 pb-4 border-b-2 border-blue-100"
              >
                <div
                  class="bg-blue text-white rounded-full w-10 h-10 flex items-center justify-center font-bold"
                >
                  1
                </div>
                <h3 class="text-2xl font-bold text-gray-800">
                  Personal Information
                </h3>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.full_name"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.email"
                    type="email"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.phone_number"
                    type="tel"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="08012345678"
                  />
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Date of Birth <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.date_of_birth"
                    type="date"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Gender <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="registrationForm.gender"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Marital Status <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="registrationForm.marital_status"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Marital Status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Qualification <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="registrationForm.qualification"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Qualification</option>
                    <option value="bsc">BSc</option>
                    <option value="msc">MSc</option>
                    <option value="phd">PhD</option>
                    <option value="diploma">Diploma</option>
                    <option value="ssce">SSCE</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Occupation <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.occupation"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Enter your occupation"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 gap-6">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Residential Address <span class="text-red-500">*</span>
                  </label>
                  <textarea
                    v-model="registrationForm.residential_address"
                    required
                    rows="2"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Enter your residential address"
                  ></textarea>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      class="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      State of Residence <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="registrationForm.state_of_residence"
                      type="text"
                      required
                      class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="Enter your state"
                    />
                  </div>

                  <div>
                    <label
                      class="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Social Media Handles <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="registrationForm.social_media_handles"
                      type="text"
                      required
                      class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="@username or profile link"
                    />
                  </div>
                </div>
              </div>

              <!-- File Uploads -->
              <div class="space-y-4">
                <!-- ID Type Dropdown -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Type of Identification <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="registrationForm.valid_means_of_identification"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select ID Type</option>
                    <option value="National ID">National ID</option>
                    <option value="Driver's License">Driver's License</option>
                    <option value="International Passport">
                      International Passport
                    </option>
                    <option value="Voter's Card">Voter's Card</option>
                  </select>
                </div>

                <!-- ID Document Upload -->
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition"
                >
                  <label class="block text-sm font-semibold text-gray-700 mb-3">
                    <span class="flex items-center">
                      <svg
                        class="w-5 h-5 mr-2 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      Upload Identification Document
                      <span class="text-red-500">*</span>
                    </span>
                  </label>
                  <input
                    @change="
                      handleFileUpload(
                        $event,
                        'valid_means_of_identification_file'
                      )
                    "
                    type="file"
                    required
                    accept=".jpg,.jpeg,.png,.pdf"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p class="text-xs text-gray-500 mt-2">
                    📄 Upload your ID document (JPG, PNG, PDF - Max 2MB)
                  </p>
                </div>

                <!-- Passport Photo Upload -->
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition"
                >
                  <label class="block text-sm font-semibold text-gray-700 mb-3">
                    <span class="flex items-center">
                      <svg
                        class="w-5 h-5 mr-2 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Passport Photograph <span class="text-red-500">*</span>
                    </span>
                  </label>
                  <input
                    @change="handleFileUpload($event, 'passport')"
                    type="file"
                    required
                    accept=".jpg,.jpeg,.png"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p class="text-xs text-gray-500 mt-2">
                    📸 Recent passport photograph (JPG, PNG - Max 2MB)
                  </p>
                </div>
              </div>
            </div>

            <!-- Section 2: Business Information -->
            <div class="space-y-6">
              <div
                class="flex items-center space-x-3 pb-4 border-b-2 border-green-100"
              >
                <div
                  class="bg-blue text-white rounded-full w-10 h-10 flex items-center justify-center font-bold"
                >
                  2
                </div>
                <h3 class="text-2xl font-bold text-gray-800">
                  Business Information
                </h3>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Business Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.business_name"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Enter business name"
                  />
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Business Sector/Industry <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="registrationForm.business_sector_industry"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Business Sector</option>
                    <option
                      v-for="(sector, index) in businessData"
                      :key="index"
                      :value="sector"
                    >
                      {{ sector }}
                    </option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Business Stage
                  </label>
                  <select
                    v-model="registrationForm.business_stage"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Business Stage</option>
                    <option value="idea">Idea Stage</option>
                    <option value="startup">Startup (0-2 years)</option>
                    <option value="growth">Growth Stage (3-5 years)</option>
                    <option value="established">Established (5+ years)</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Years in Operation <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.years_in_operation"
                    type="number"
                    min="0"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="e.g., 3"
                  />
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Annual Revenue Range
                  </label>
                  <select
                    v-model="registrationForm.annual_revenue_range"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Revenue Range</option>
                    <option value="0-1m">₦0 - ₦1M</option>
                    <option value="1m-5m">₦1M - ₦5M</option>
                    <option value="5m-10m">₦5M - ₦10M</option>
                    <option value="10m-50m">₦10M - ₦50M</option>
                    <option value="50m-100m">₦50M - ₦100M</option>
                    <option value="100m+">₦100M+</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Team Size <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="registrationForm.team_size"
                    type="number"
                    min="0"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Number of employees"
                  />
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Is Business Registered? <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="registrationForm.is_business_registered"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                <div
                  v-if="registrationForm.is_business_registered === 'yes'"
                  class="md:col-span-2"
                >
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition"
                  >
                    <label
                      class="block text-sm font-semibold text-gray-700 mb-3"
                    >
                      <span class="flex items-center">
                        <svg
                          class="w-5 h-5 mr-2 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        CAC Registration Document
                      </span>
                    </label>
                    <input
                      @change="handleFileUpload($event, 'cac_document')"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    />
                    <p class="text-xs text-gray-500 mt-2">
                      📋 Upload CAC Certificate (PDF, JPG, PNG - Max 2MB)
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                  Business Description <span class="text-red-500">*</span>
                  <span class="text-xs text-gray-500 font-normal"
                    >(Max 250 words)</span
                  >
                </label>
                <textarea
                  v-model="registrationForm.business_description"
                  @input="handleTextInput('business_description', 250)"
                  required
                  rows="4"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Describe your business, products/services, and target market"
                ></textarea>
                <div class="flex justify-between items-center mt-1">
                  <p class="text-xs text-gray-500">
                    Tell us about what your business does and who you serve
                  </p>
                  <p
                    class="text-sm font-medium"
                    :class="{
                      'text-red-600': wordCount.business_description > 250,
                      'text-green-600':
                        wordCount.business_description > 0 &&
                        wordCount.business_description <= 250,
                      'text-gray-400': wordCount.business_description === 0,
                    }"
                  >
                    {{ wordCount.business_description }} / 250 words
                  </p>
                </div>
              </div>
            </div>

            <!-- Section 3: Program Application -->
            <div class="space-y-6">
              <div
                class="flex items-center space-x-3 pb-4 border-b-2 border-purple-100"
              >
                <div
                  class="bg-blue text-white rounded-full w-10 h-10 flex items-center justify-center font-bold"
                >
                  3
                </div>
                <h3 class="text-2xl font-bold text-gray-800">
                  Program Application
                </h3>
              </div>

              <div class="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                <p class="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Take your time with these questions.
                  They help us understand your goals and how we can best support
                  your business journey.
                </p>
              </div>

              <div class="space-y-6">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Why do you want to join the BUM Masterclass?
                    <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 font-normal"
                      >(Max 250 words)</span
                    >
                  </label>
                  <textarea
                    v-model="registrationForm.reason_for_joining_masterclass"
                    @input="
                      handleTextInput('reason_for_joining_masterclass', 250)
                    "
                    required
                    rows="4"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Share your motivation for joining this program..."
                  ></textarea>
                  <p
                    class="text-sm mt-1 text-right font-medium"
                    :class="{
                      'text-red-600':
                        wordCount.reason_for_joining_masterclass > 250,
                      'text-green-600':
                        wordCount.reason_for_joining_masterclass > 0 &&
                        wordCount.reason_for_joining_masterclass <= 250,
                      'text-gray-400':
                        wordCount.reason_for_joining_masterclass === 0,
                    }"
                  >
                    {{ wordCount.reason_for_joining_masterclass }} / 250 words
                  </p>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    What do you expect to learn and how will you apply it?
                    <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 font-normal"
                      >(Max 250 words)</span
                    >
                  </label>
                  <textarea
                    v-model="registrationForm.expected_learning_and_application"
                    @input="
                      handleTextInput('expected_learning_and_application', 250)
                    "
                    required
                    rows="4"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Describe what you hope to learn and how you'll implement it..."
                  ></textarea>
                  <p
                    class="text-sm mt-1 text-right font-medium"
                    :class="{
                      'text-red-600':
                        wordCount.expected_learning_and_application > 250,
                      'text-green-600':
                        wordCount.expected_learning_and_application > 0 &&
                        wordCount.expected_learning_and_application <= 250,
                      'text-gray-400':
                        wordCount.expected_learning_and_application === 0,
                    }"
                  >
                    {{ wordCount.expected_learning_and_application }} / 250
                    words
                  </p>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    What is your vision for your business in the next 1-3 years?
                    <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 font-normal"
                      >(Max 250 words)</span
                    >
                  </label>
                  <textarea
                    v-model="registrationForm.business_vision_next_1_to_3_years"
                    @input="
                      handleTextInput('business_vision_next_1_to_3_years', 250)
                    "
                    required
                    rows="4"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Paint a picture of where you see your business..."
                  ></textarea>
                  <p
                    class="text-sm mt-1 text-right font-medium"
                    :class="{
                      'text-red-600':
                        wordCount.business_vision_next_1_to_3_years > 250,
                      'text-green-600':
                        wordCount.business_vision_next_1_to_3_years > 0 &&
                        wordCount.business_vision_next_1_to_3_years <= 250,
                      'text-gray-400':
                        wordCount.business_vision_next_1_to_3_years === 0,
                    }"
                  >
                    {{ wordCount.business_vision_next_1_to_3_years }} / 250
                    words
                  </p>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Where are you currently and what steps have you taken?
                    <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 font-normal"
                      >(Max 250 words)</span
                    >
                  </label>
                  <textarea
                    v-model="
                      registrationForm.current_stage_of_vision_and_steps_taken
                    "
                    @input="
                      handleTextInput(
                        'current_stage_of_vision_and_steps_taken',
                        250
                      )
                    "
                    required
                    rows="4"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Share your progress and actions taken so far..."
                  ></textarea>
                  <p
                    class="text-sm mt-1 text-right font-medium"
                    :class="{
                      'text-red-600':
                        wordCount.current_stage_of_vision_and_steps_taken > 250,
                      'text-green-600':
                        wordCount.current_stage_of_vision_and_steps_taken > 0 &&
                        wordCount.current_stage_of_vision_and_steps_taken <=
                          250,
                      'text-gray-400':
                        wordCount.current_stage_of_vision_and_steps_taken === 0,
                    }"
                  >
                    {{ wordCount.current_stage_of_vision_and_steps_taken }} /
                    250 words
                  </p>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    What are the main challenges you're facing?
                    <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 font-normal"
                      >(Max 250 words)</span
                    >
                  </label>
                  <textarea
                    v-model="registrationForm.challenges"
                    @input="handleTextInput('challenges', 250)"
                    required
                    rows="4"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Be specific about the obstacles you're encountering..."
                  ></textarea>
                  <p
                    class="text-sm mt-1 text-right font-medium"
                    :class="{
                      'text-red-600': wordCount.challenges > 250,
                      'text-green-600':
                        wordCount.challenges > 0 && wordCount.challenges <= 250,
                      'text-gray-400': wordCount.challenges === 0,
                    }"
                  >
                    {{ wordCount.challenges }} / 250 words
                  </p>
                </div>

                <!-- Personal Statement - Changed to FILE UPLOAD -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Personal Statement (Upload Document)
                    <span class="text-red-500">*</span>
                  </label>
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-purple-500 transition"
                  >
                    <label
                      class="block text-sm font-semibold text-gray-700 mb-3"
                    >
                      <span class="flex items-center">
                        <svg
                          class="w-5 h-5 mr-2 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Upload Personal Statement Document
                      </span>
                    </label>
                    <input
                      @change="handleFileUpload($event, 'personal_statement')"
                      type="file"
                      required
                      accept=".pdf,.doc,.docx"
                      class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    />
                    <p class="text-xs text-gray-500 mt-2">
                      📝 Upload a document about yourself and your
                      entrepreneurial journey (PDF, DOC, DOCX - Max 2MB)
                    </p>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Any additional expectations or comments?
                    <span class="text-xs text-gray-500 font-normal"
                      >(Optional - Max 250 words)</span
                    >
                  </label>
                  <textarea
                    v-model="registrationForm.expectations"
                    @input="handleTextInput('expectations', 250)"
                    rows="3"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Anything else you'd like us to know..."
                  ></textarea>
                  <p
                    class="text-sm mt-1 text-right font-medium"
                    :class="{
                      'text-red-600': wordCount.expectations > 250,
                      'text-green-600':
                        wordCount.expectations > 0 &&
                        wordCount.expectations <= 250,
                      'text-gray-400': wordCount.expectations === 0,
                    }"
                  >
                    {{ wordCount.expectations }} / 250 words
                  </p>
                </div>
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
              <div class="flex items-start space-x-3">
                <input
                  v-model="registrationForm.agreeToTerms"
                  type="checkbox"
                  required
                  class="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label class="text-sm text-gray-700">
                  <strong>Important Commitment:</strong> I confirm my
                  availability to attend all 8 weeks and commit to full
                  participation in the program. I also confirm that all
                  information provided is accurate and truthful.
                  <span class="text-red-500">*</span>
                </label>
              </div>
            </div>

            <!-- Error Message -->
            <div
              v-if="errorMessage"
              class="bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-lg flex items-start"
            >
              <svg
                class="w-6 h-6 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
              <div>{{ errorMessage }}</div>
            </div>

            <!-- Success Message -->
            <div
              v-if="successMessage"
              class="bg-green-50 border-l-4 border-green-500 text-green-700 px-6 py-4 rounded-lg flex items-start"
            >
              <svg
                class="w-6 h-6 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <div>{{ successMessage }}</div>
            </div>

            <!-- Submit Button -->
            <div
              class="bottom-0 bg-white pt-6 pb-2 -mx-6 px-6 md:-mx-10 md:px-10 border-t border-gray-200 flex flex-col items-center"
            >
              <button
                type="submit"
                :disabled="isSubmitting || isLoadingCost"
                class="bg-blue hover:bg-blue text-white font-bold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <span
                  v-if="!isSubmitting && !isLoadingCost"
                  class="flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Proceed to register
                </span>
                <span
                  v-else-if="isLoadingCost"
                  class="flex items-center justify-center"
                >
                  <svg class="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                      fill="none"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading cost...
                </span>
                <span v-else class="flex items-center justify-center">
                  <svg class="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                      fill="none"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing Registration...
                </span>
              </button>
              <p class="text-center text-xs text-gray-500 mt-3">
                🔒 Secure payment powered by Paystack
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section
      class="container py-20 px-4 mx-auto max-w-screen-xl bg-gradient-to-br from-gray-50 to-gray-100"
    >
      <div class="text-center mb-12">
        <h2 class="text-4xl font-bold text-gray-800 sm:text-5xl mb-4">
          Ready to Transform Your Business?
        </h2>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Take the next step in your entrepreneurial journey. Apply now or
          contact us for more information.
        </p>
      </div>

      <div class="max-w-2xl mx-auto">
        <div class="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div class="space-y-6">
            <!-- Email Contact -->
            <div
              class="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300"
            >
              <div class="flex-shrink-0">
                <div
                  class="w-14 h-14 bg-blue rounded-full flex items-center justify-center"
                >
                  <i class="fa-solid fa-envelope text-white text-2xl"></i>
                </div>
              </div>
              <div>
                <h4 class="font-semibold text-gray-800 mb-1">
                  Admissions Email
                </h4>
                <a
                  href="mailto:bum@barkaumshelia.com"
                  class="text-blue hover:underline text-lg"
                >
                  bum@barkaumshelia.com
                </a>
              </div>
            </div>

            <!-- Phone Contact -->
            <div
              class="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300"
            >
              <div class="flex-shrink-0">
                <div
                  class="w-14 h-14 bg-blue rounded-full flex items-center justify-center"
                >
                  <i class="fa-solid fa-phone text-white text-2xl"></i>
                </div>
              </div>
              <div>
                <h4 class="font-semibold text-gray-800 mb-1">Contact Number</h4>
                <a
                  href="tel:09114446444"
                  class="text-blue hover:underline text-lg"
                >
                  09114446444
                </a>
              </div>
            </div>
          </div>

          <!-- CTA Button -->
          <div class="mt-10 text-center">
            <button
              @click="scrollToRegistration"
              class="inline-block bg-gradient-to-r from-blue to-sky-600 hover:from-sky-600 hover:to-blue text-white font-bold px-10 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              ➡ Apply Now for BUM Masterclass
            </button>
          </div>
        </div>
      </div>
    </section>
  </main>
  <router-view />
</template>

<script>
import apiService from "../services/apiService";
import Swal from "sweetalert2";
import {
  showErrorAlert,
  showLoadingAlert,
  showSuccessAlert,
} from "../services/reusableService";

export default {
  name: "bum-masterclass",
  data() {
    return {
      registrationForm: {
        training_type_id: "",
        full_name: "",
        email: "",
        phone_number: "",
        residential_address: "",
        date_of_birth: "",
        gender: "",
        marital_status: "",
        valid_means_of_identification: "",
        social_media_handles: "",
        business_name: "",
        business_sector_industry: "",
        business_description: "",
        business_stage: "",
        years_in_operation: "",
        annual_revenue_range: "",
        is_business_registered: "",
        team_size: "",
        reason_for_joining_masterclass: "",
        expected_learning_and_application: "",
        business_vision_next_1_to_3_years: "",
        current_stage_of_vision_and_steps_taken: "",
        occupation: "",
        challenges: "",
        qualification: "",
        state_of_residence: "",
        expectations: "",
        agreeToTerms: false,
      },
      // Separate file storage
      files: {
        valid_means_of_identification_file: null,
        cac_document: null,
        passport: null,
        personal_statement: null,
      },
      isSubmitting: false,
      errorMessage: "",
      successMessage: "",
      wordCount: {
        expectations: 0,
        reason_for_joining_masterclass: 0,
        expected_learning_and_application: 0,
        business_vision_next_1_to_3_years: 0,
        current_stage_of_vision_and_steps_taken: 0,
        challenges: 0,
        business_description: 0,
      },
      businessData: [],
      bumMasterclassId: null,
      bumMasterclassCost: null,
      bumMasterclassCostKobo: 0,
      isLoadingCost: true,
    };
  },
  mounted() {
    this.scrollToTop();
    this.fetchBusinessSectors();
    this.fetchBumMasterclassId();
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    scrollToRegistration() {
      const element = document.getElementById("registration");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    scrollToDetails() {
      const element = document.getElementById("details");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    async fetchBusinessSectors() {
      try {
        const response = await apiService.getBusinessSector();
        this.businessData = response.data.data || [];
      } catch (error) {
        console.error("Failed to fetch business sectors:", error);
      }
    },
    async fetchBumMasterclassId() {
      try {
        const response = await apiService.getTrainingTypeMmc();
        const trainingData = response.data.data || [];

        const bumMasterclass = trainingData.find(
          (item) => item.name && item.name.toLowerCase().includes("bum")
        );

        if (bumMasterclass) {
          this.bumMasterclassId = bumMasterclass.id;
          this.registrationForm.training_type_id = bumMasterclass.id;

          console.log("BUM Masterclass Data:", bumMasterclass);
          console.log("BUM Masterclass ID:", this.bumMasterclassId);

          if (bumMasterclass.cost) {
            const costValue = parseFloat(bumMasterclass.cost);
            this.bumMasterclassCostKobo = costValue * 100;
            this.bumMasterclassCost = this.formatCurrency(costValue);

            console.log("BUM Masterclass Cost:", this.bumMasterclassCost);
            console.log("Cost in Kobo:", this.bumMasterclassCostKobo);
          } else {
            showErrorAlert(
              "Program cost not available. Please contact support."
            );
          }

          this.isLoadingCost = false;
        } else {
          this.isLoadingCost = false;
          showErrorAlert("BUM Masterclass program not found.");
        }
      } catch (error) {
        console.error("Failed to fetch BUM Masterclass ID:", error);
        this.isLoadingCost = false;
        showErrorAlert(
          "Failed to load program details. Please refresh the page."
        );
      }
    },
    formatCurrency(amount) {
      return `₦${amount.toLocaleString("en-NG")}`;
    },

    handleFileUpload(event, fieldName) {
      const file = event.target.files[0];
      if (!file) {
        console.log(`No file selected for ${fieldName}`);
        return;
      }

      console.log(`Handling file upload for ${fieldName}:`, {
        name: file.name,
        type: file.type,
        size: file.size,
      });

      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showErrorAlert("File size must be less than 2MB");
        event.target.value = "";
        this.files[fieldName] = null;
        return;
      }

      // More lenient file type checking (some browsers report different MIME types)
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const acceptedExtensions = {
        passport: ["jpg", "jpeg", "png"],
        valid_means_of_identification_file: ["jpg", "jpeg", "png", "pdf"],
        personal_statement: ["pdf", "doc", "docx"],
        cac_document: ["jpg", "jpeg", "png", "pdf"],
      };

      if (
        acceptedExtensions[fieldName] &&
        !acceptedExtensions[fieldName].includes(fileExtension)
      ) {
        const allowedFormats = {
          passport: "JPG, JPEG, PNG",
          valid_means_of_identification_file: "JPG, JPEG, PNG, PDF",
          personal_statement: "PDF, DOC, DOCX",
          cac_document: "JPG, JPEG, PNG, PDF",
        };
        showErrorAlert(
          `Please upload a valid file format: ${allowedFormats[fieldName]}`
        );
        event.target.value = "";
        this.files[fieldName] = null;
        return;
      }

      // Store file - Vue 3 way (direct assignment triggers reactivity)
      this.files[fieldName] = file;
      console.log(`✓ File stored successfully for ${fieldName}:`, file.name);
      console.log("Current files object:", this.files);
    },

    handleTextInput(fieldName, maxWords) {
      const text = this.registrationForm[fieldName];
      const count = this.countWords(text);
      this.wordCount[fieldName] = count;

      if (count > maxWords) {
        const words = text.trim().split(/\s+/);
        this.registrationForm[fieldName] = words.slice(0, maxWords).join(" ");
        this.wordCount[fieldName] = maxWords;
        showErrorAlert(`You can only enter up to ${maxWords} words.`);
      }
    },
    countWords(text) {
      if (!text) return 0;
      const words = text
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0);
      return words.length;
    },
    validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    validatePhoneNumber(phone) {
      const phoneRegex = /^0?[7-9][0-1]\d{8}$/;
      return phoneRegex.test(phone.replace(/\s+/g, ""));
    },
    async submitRegistration() {
      try {
        this.errorMessage = "";
        this.successMessage = "";
        this.isSubmitting = true;

        console.log("=== Registration Debug Start ===");
        console.log("Form Data:", this.registrationForm);
        console.log("Files:", this.files);

        // Validation checks
        if (!this.registrationForm.training_type_id) {
          this.errorMessage =
            "Unable to identify training program. Please contact customer support: 09114446444 or send us an email bum@barkaumshelia.com.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        if (!this.bumMasterclassCostKobo) {
          this.errorMessage =
            "Program cost not loaded. Please refresh and try again.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        // Validate email format
        if (!this.validateEmail(this.registrationForm.email)) {
          this.errorMessage = "Please enter a valid email address.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        // Validate phone number format
        if (!this.validatePhoneNumber(this.registrationForm.phone_number)) {
          this.errorMessage = "Please enter a valid Nigerian phone number.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        // Validate identification type selection
        if (!this.registrationForm.valid_means_of_identification) {
          this.errorMessage =
            "Please select a valid means of identification type.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        // Validate file uploads
        if (!this.files.valid_means_of_identification_file) {
          this.errorMessage = "Please upload your identification document.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        if (!this.files.passport) {
          this.errorMessage = "Please upload your passport photograph.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        if (!this.files.personal_statement) {
          this.errorMessage = "Please upload your personal statement document.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        // Validate required fields
        const requiredFields = [
          { key: "full_name", label: "Full Name" },
          { key: "email", label: "Email" },
          { key: "phone_number", label: "Phone Number" },
          { key: "residential_address", label: "Residential Address" },
          { key: "date_of_birth", label: "Date of Birth" },
          { key: "gender", label: "Gender" },
          { key: "marital_status", label: "Marital Status" },
          { key: "social_media_handles", label: "Social Media Handles" },
          { key: "business_name", label: "Business Name" },
          {
            key: "business_sector_industry",
            label: "Business Sector/Industry",
          },
          { key: "business_description", label: "Business Description" },
          // { key: "business_stage", label: "Business Stage" },
          { key: "years_in_operation", label: "Years in Operation" },
          // { key: "annual_revenue_range", label: "Annual Revenue Range" },
          {
            key: "is_business_registered",
            label: "Business Registration Status",
          },
          { key: "team_size", label: "Team Size" },
          {
            key: "reason_for_joining_masterclass",
            label: "Reason for Joining",
          },
          {
            key: "expected_learning_and_application",
            label: "Expected Learning",
          },
          {
            key: "business_vision_next_1_to_3_years",
            label: "Business Vision",
          },
          {
            key: "current_stage_of_vision_and_steps_taken",
            label: "Current Stage of Vision",
          },
          { key: "occupation", label: "Occupation" },
          { key: "challenges", label: "Challenges" },
          { key: "qualification", label: "Qualification" },
          { key: "state_of_residence", label: "State of Residence" },
        ];

        const missingFields = requiredFields.filter(
          (field) => !this.registrationForm[field.key]
        );

        if (missingFields.length > 0) {
          const missingLabels = missingFields
            .map((field) => field.label)
            .join(", ");
          this.errorMessage = `Please fill in all required fields: ${missingLabels}`;
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        // Validate CAC document if business is registered
        if (
          this.registrationForm.is_business_registered === "yes" &&
          !this.files.cac_document
        ) {
          this.errorMessage = "Please upload your CAC registration document.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        if (!this.registrationForm.agreeToTerms) {
          this.errorMessage = "Please agree to the terms and conditions.";
          showErrorAlert(this.errorMessage);
          this.isSubmitting = false;
          return;
        }

        showLoadingAlert("Processing your registration...");

        // Prepare FormData for file uploads
        const formData = new FormData();

        // Add all text fields
        formData.append("full_name", this.registrationForm.full_name.trim());
        formData.append(
          "email",
          this.registrationForm.email.trim().toLowerCase()
        );
        formData.append(
          "phone_number",
          this.registrationForm.phone_number.trim()
        );
        formData.append(
          "residential_address",
          this.registrationForm.residential_address.trim()
        );
        formData.append("date_of_birth", this.registrationForm.date_of_birth);
        formData.append("gender", this.registrationForm.gender);
        formData.append("marital_status", this.registrationForm.marital_status);
        formData.append(
          "valid_means_of_identification",
          this.registrationForm.valid_means_of_identification
        );
        formData.append(
          "social_media_handles",
          this.registrationForm.social_media_handles.trim()
        );
        formData.append(
          "business_name",
          this.registrationForm.business_name.trim()
        );
        formData.append(
          "business_sector_industry",
          this.registrationForm.business_sector_industry
        );
        formData.append(
          "business_description",
          this.registrationForm.business_description.trim()
        );
        formData.append("business_stage", this.registrationForm.business_stage);
        formData.append(
          "years_in_operation",
          this.registrationForm.years_in_operation
        );
        formData.append(
          "annual_revenue_range",
          this.registrationForm.annual_revenue_range
        );
        formData.append(
          "is_business_registered",
          this.registrationForm.is_business_registered
        );
        formData.append("team_size", this.registrationForm.team_size);
        formData.append(
          "reason_for_joining_masterclass",
          this.registrationForm.reason_for_joining_masterclass.trim()
        );
        formData.append(
          "expected_learning_and_application",
          this.registrationForm.expected_learning_and_application.trim()
        );
        formData.append(
          "business_vision_next_1_to_3_years",
          this.registrationForm.business_vision_next_1_to_3_years.trim()
        );
        formData.append(
          "current_stage_of_vision_and_steps_taken",
          this.registrationForm.current_stage_of_vision_and_steps_taken.trim()
        );
        formData.append("occupation", this.registrationForm.occupation.trim());
        formData.append("challenges", this.registrationForm.challenges.trim());
        formData.append("qualification", this.registrationForm.qualification);
        formData.append(
          "state_of_residence",
          this.registrationForm.state_of_residence.trim()
        );
        formData.append(
          "expectations",
          this.registrationForm.expectations?.trim() || ""
        );
        formData.append(
          "training_type_id",
          this.registrationForm.training_type_id
        );

        console.log("=== Preparing File Uploads ===");
        console.log("Files object:", this.files);

        // Add file uploads - with explicit checks and filename
        if (this.files.passport) {
          console.log("Appending passport:", this.files.passport.name);
          formData.append(
            "passport",
            this.files.passport,
            this.files.passport.name
          );
        } else {
          console.error("ERROR: passport file is missing!");
        }

        if (this.files.personal_statement) {
          console.log(
            "Appending personal_statement:",
            this.files.personal_statement.name
          );
          formData.append(
            "personal_statement",
            this.files.personal_statement,
            this.files.personal_statement.name
          );
        } else {
          console.error("ERROR: personal_statement file is missing!");
        }

        if (this.files.valid_means_of_identification_file) {
          console.log(
            "Appending valid_means_of_identification_file:",
            this.files.valid_means_of_identification_file.name
          );
          formData.append(
            "valid_means_of_identification_file",
            this.files.valid_means_of_identification_file,
            this.files.valid_means_of_identification_file.name
          );
        } else {
          console.error(
            "ERROR: valid_means_of_identification_file is missing!"
          );
        }

        if (
          this.registrationForm.is_business_registered === "yes" &&
          this.files.cac_document
        ) {
          console.log("Appending cac_document:", this.files.cac_document.name);
          formData.append(
            "cac_document",
            this.files.cac_document,
            this.files.cac_document.name
          );
        }

        // Log FormData contents for debugging
        console.log("=== FormData Contents ===");
        for (let pair of formData.entries()) {
          if (pair[1] instanceof File) {
            console.log(
              `${pair[0]}: [FILE] ${pair[1].name} (${pair[1].type}, ${pair[1].size} bytes)`
            );
          } else {
            console.log(`${pair[0]}: ${pair[1]}`);
          }
        }

        console.log("=== Sending request to API ===");
        const response = await apiService.createRegistration(formData);

        console.log("Registration Response:", response);

        Swal.close();

        if (response && response.data && response.data.status === true) {
          console.log("Registration successful");
          this.successMessage =
            "Registration successful! We will contact you with next steps.";
             this.resetForm();
        } else {
          console.warn("Registration response:", response.data);
          this.errorMessage =
            response.data?.message ||
            "Registration was not successful. Please try again.";
          showErrorAlert(this.errorMessage);
        }
      } catch (error) {
        Swal.close();

        console.error("=== Registration Error ===");
        console.error("Full Error:", error);
        console.error("Error Response:", error.response);

        let errorMessage = "Failed to submit registration. Please try again.";

        if (error.response?.data) {
          console.error("Server Error Data:", error.response.data);

          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }

          // Check for validation errors
          if (error.response.data.errors) {
            const errors = error.response.data.errors;
            console.error("Validation Errors:", errors);
            const errorMessages = Object.entries(errors).map(
              ([field, msgs]) => {
                return `${field}: ${
                  Array.isArray(msgs) ? msgs.join(", ") : msgs
                }`;
              }
            );
            errorMessage = errorMessages.join("\n");
          }
        } else if (error.request) {
          errorMessage =
            "No response from server. Please check your internet connection.";
        }

        this.errorMessage = errorMessage;
        showErrorAlert(errorMessage);
      } finally {
        this.isSubmitting = false;
        console.log("=== Registration Debug End ===");
      }
    },
    resetForm() {
      this.registrationForm = {
        training_type_id: this.bumMasterclassId || "",
        full_name: "",
        email: "",
        phone_number: "",
        residential_address: "",
        date_of_birth: "",
        gender: "",
        marital_status: "",
        valid_means_of_identification: "",
        social_media_handles: "",
        business_name: "",
        business_sector_industry: "",
        business_description: "",
        business_stage: "",
        years_in_operation: "",
        annual_revenue_range: "",
        is_business_registered: "",
        team_size: "",
        reason_for_joining_masterclass: "",
        expected_learning_and_application: "",
        business_vision_next_1_to_3_years: "",
        current_stage_of_vision_and_steps_taken: "",
        occupation: "",
        challenges: "",
        qualification: "",
        state_of_residence: "",
        expectations: "",
        agreeToTerms: false,
      };

      // Reset files
      this.files = {
        valid_means_of_identification_file: null,
        cac_document: null,
        passport: null,
        personal_statement: null,
      };

      // Reset word counts
      Object.keys(this.wordCount).forEach((key) => {
        this.wordCount[key] = 0;
      });

      // Reset file inputs
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input) => {
        input.value = "";
      });

      this.errorMessage = "";
      this.successMessage = "";
    },
  },
};
</script>

<style scoped>
.card {
  transition: transform 0.2s ease;
}
.card:hover {
  transform: scale(1.02);
}

/* Animation delays */
.animation-delay-200 {
  animation-delay: 0.2s;
}
.animation-delay-400 {
  animation-delay: 0.4s;
}
.animation-delay-600 {
  animation-delay: 0.6s;
}
.animation-delay-2000 {
  animation-delay: 2s;
}
.animation-delay-4000 {
  animation-delay: 4s;
}

/* Blob animation */
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

svg path {
  fill: #fff !important;
  stroke: none !important;
  box-shadow: none !important;
}

svg {
  overflow: hidden !important;
}

.text-lg {
  font-size: 20px !important;
}

.overflow-hidden {
  overflow: hidden !important;
}
</style>
